import { doc, getDoc, onSnapshot, Unsubscribe, collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  getGalleryFavoritePiecePath,
  getGalleryFavoritePiecesPath
} from '@shared/constants/firebase-paths';
import { useAuthStore } from '../stores/authStore';

class FavoritesService {
  private favoritesCache = new Map<string, boolean>();
  private unsubscribers = new Map<string, Unsubscribe>();
  private callbacks = new Map<string, Set<(isFavorited: boolean) => void>>();

  private resolveGalleryId(galleryId?: string): string | undefined {
    return galleryId ?? useAuthStore.getState().galleryId ?? undefined;
  }

  private getCacheKey(userId: string, pieceId: string, galleryId?: string): string {
    return `${galleryId ?? 'unknown'}_${userId}_${pieceId}`;
  }

  private notifyCallbacks(cacheKey: string, isFavorited: boolean): void {
    const callbackSet = this.callbacks.get(cacheKey);
    if (callbackSet) {
      callbackSet.forEach(cb => cb(isFavorited));
    }
  }

  /**
   * Check if a piece is favorited by the user
   */
  async isFavorited(userId: string, pieceId: string, galleryId?: string): Promise<boolean> {
    const resolvedGalleryId = this.resolveGalleryId(galleryId);
    const cacheKey = this.getCacheKey(userId, pieceId, resolvedGalleryId);
    
    // Return cached result if available
    if (this.favoritesCache.has(cacheKey)) {
      return this.favoritesCache.get(cacheKey)!;
    }

    try {
      if (!resolvedGalleryId) {
        this.favoritesCache.set(cacheKey, false);
        return false;
      }

      const galleryFavDocRef = doc(db, getGalleryFavoritePiecePath(resolvedGalleryId, userId, pieceId));
      const galleryFavDoc = await getDoc(galleryFavDocRef);
      const isFavorited = galleryFavDoc.exists() && galleryFavDoc.data()?.isOn === true;
      this.favoritesCache.set(cacheKey, isFavorited);
      return isFavorited;
    } catch (error) {
      console.error('Error checking favorite status:', error);
      return false;
    }
  }

  /**
   * Get favorites for multiple pieces (for grid/list view)
   */
  async getFavoritesForPieces(userId: string, pieceIds: string[], galleryId?: string): Promise<Map<string, boolean>> {
    const resolvedGalleryId = this.resolveGalleryId(galleryId);
    const favoritesMap = new Map<string, boolean>();
    
    // Check cache first
    const uncachedPieceIds: string[] = [];
    for (const pieceId of pieceIds) {
      const cacheKey = this.getCacheKey(userId, pieceId, resolvedGalleryId);
      if (this.favoritesCache.has(cacheKey)) {
        favoritesMap.set(pieceId, this.favoritesCache.get(cacheKey)!);
      } else {
        uncachedPieceIds.push(pieceId);
      }
    }

    // Fetch uncached favorites
    const promises = uncachedPieceIds.map(async (pieceId) => {
      const isFavorited = await this.isFavorited(userId, pieceId, resolvedGalleryId);
      favoritesMap.set(pieceId, isFavorited);
      return { pieceId, isFavorited };
    });

    await Promise.all(promises);
    return favoritesMap;
  }

  /**
   * Subscribe to real-time favorite status changes for a piece
   */
  subscribeToFavoriteStatus(
    userId: string, 
    pieceId: string, 
    callback: (isFavorited: boolean) => void,
    galleryId?: string
  ): Unsubscribe {
    const resolvedGalleryId = this.resolveGalleryId(galleryId);
    const cacheKey = this.getCacheKey(userId, pieceId, resolvedGalleryId);
    
    // Add callback to the set for this piece
    if (!this.callbacks.has(cacheKey)) {
      this.callbacks.set(cacheKey, new Set());
    }
    this.callbacks.get(cacheKey)!.add(callback);
    
    // Only create one Firestore subscription per piece
    if (!this.unsubscribers.has(cacheKey)) {
      if (resolvedGalleryId) {
        const galleryFavDocRef = doc(db, getGalleryFavoritePiecePath(resolvedGalleryId, userId, pieceId));
        const unsubscribeGallery = onSnapshot(galleryFavDocRef, (snapshot) => {
          const isFavorited = snapshot.exists() && snapshot.data()?.isOn === true;
          this.favoritesCache.set(cacheKey, isFavorited);
          this.notifyCallbacks(cacheKey, isFavorited);
        }, (error) => {
          console.error('Error subscribing to gallery favorite status:', error);
          this.favoritesCache.set(cacheKey, false);
          this.notifyCallbacks(cacheKey, false);
        });

        this.unsubscribers.set(cacheKey, unsubscribeGallery);
      } else {
        this.favoritesCache.set(cacheKey, false);
        this.notifyCallbacks(cacheKey, false);
        this.unsubscribers.set(cacheKey, () => {});
      }
    } else {
      // Subscription already exists, just call the callback with current cached value
      const cached = this.favoritesCache.get(cacheKey);
      if (cached !== undefined) {
        callback(cached);
      }
    }
    
    // Return unsubscribe function that only removes this callback
    return () => {
      const callbackSet = this.callbacks.get(cacheKey);
      if (callbackSet) {
        callbackSet.delete(callback);
        
        // If no more callbacks, clean up the Firestore subscription
        if (callbackSet.size === 0) {
          this.callbacks.delete(cacheKey);
          const unsubscribe = this.unsubscribers.get(cacheKey);
          if (unsubscribe) {
            unsubscribe();
            this.unsubscribers.delete(cacheKey);
          }
        }
      }
    };
  }

  /**
   * Clear cache for a specific user-piece combination
   */
  clearCache(userId: string, pieceId: string, galleryId?: string): void {
    const cacheKey = this.getCacheKey(userId, pieceId, this.resolveGalleryId(galleryId));
    this.favoritesCache.delete(cacheKey);
  }

  /**
   * Clear all cache
   */
  clearAllCache(): void {
    this.favoritesCache.clear();
    
    // Clean up all subscriptions
    this.unsubscribers.forEach(unsubscribe => unsubscribe());
    this.unsubscribers.clear();
  }

  /**
   * Get all favorited pieces for a user
   */
  async getAllFavorites(userId: string, galleryId?: string): Promise<string[]> {
    const resolvedGalleryId = this.resolveGalleryId(galleryId);

    try {
      const favoritedPieceIds = new Set<string>();
      if (!resolvedGalleryId) {
        return [];
      }

      const galleryFavoritesCollectionRef = collection(db, getGalleryFavoritePiecesPath(resolvedGalleryId, userId));
      const gallerySnapshot = await getDocs(galleryFavoritesCollectionRef);

      gallerySnapshot.forEach(doc => {
        if (doc.data()?.isOn === true) {
          favoritedPieceIds.add(doc.id);
        }
      });

      return Array.from(favoritedPieceIds);
    } catch (error) {
      console.error('Error getting all favorites:', error);
      return [];
    }
  }

  /**
   * Update cache when favorite is toggled (called after successful toggle)
   */
  updateCache(userId: string, pieceId: string, isFavorited: boolean, galleryId?: string): void {
    const cacheKey = this.getCacheKey(userId, pieceId, this.resolveGalleryId(galleryId));
    this.favoritesCache.set(cacheKey, isFavorited);
    this.notifyCallbacks(cacheKey, isFavorited);
  }
}

export default new FavoritesService();
